import React, { useState, useEffect, useCallback } from 'react';
import {
  Space,
  Switch,
  InputNumber,
  Select,
  Button,
  Alert,
  Typography,
  Progress,
  Row,
  Col,
  Tag,
  Segmented,
  message
} from 'antd';
import { SaveOutlined, ExperimentOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../api';
import { useI18n } from '../i18n';

// 表单行：左侧说明 + 右侧控件
const FieldRow = ({ label, hint, children }) => (
  <Row align="middle" gutter={16}>
    <Col span={7}>
      <Typography.Text>{label}</Typography.Text>
      {hint && (
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {hint}
          </Typography.Text>
        </div>
      )}
    </Col>
    <Col span={17}>{children}</Col>
  </Row>
);

// 自动交易设置：开关、价格区间、小时目标、单笔区间、标签圈定钱包，以及试运行
const AutoTradePanel = () => {
  const { t, ts } = useI18n();
  const [config, setConfig] = useState(null);
  const [hourStats, setHourStats] = useState(null);
  const [tagOptions, setTagOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dryRunning, setDryRunning] = useState(false);
  const [dryResult, setDryResult] = useState(null);
  const [error, setError] = useState('');

  const [tagsLoading, setTagsLoading] = useState(false);

  // 单独刷新标签候选：在钱包列表新打了标签后，不离开本页即可拉到最新
  const loadTags = useCallback(async () => {
    setTagsLoading(true);
    try {
      const res = await api.get('/wallets/tags');
      setTagOptions(res.data.data.map((it) => ({ value: it.tag, label: t('（{tag}（{n} 个钱包））', { tag: it.tag, n: it.count }) })));
    } catch (err) {
      message.error(t('标签刷新失败: ') + ts(err.response?.data?.error || err.message));
    } finally {
      setTagsLoading(false);
    }
  }, [t, ts]);

  const load = useCallback(async () => {
    setError('');
    try {
      const statusRes = await api.get('/auto-trade/status');
      setConfig(statusRes.data.data.config);
      setHourStats(statusRes.data.data.hourStats);
      await loadTags();
    } catch (err) {
      setError(ts(err.response?.data?.error || err.message));
    }
  }, [loadTags, ts]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (field) => (value) => setConfig((c) => ({ ...c, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/auto-trade/config', config);
      setConfig(res.data.data);
      message.success(res.data.data.enabled ? t('已保存，自动交易运行中（每分钟评估一次）') : t('已保存，自动交易处于关闭状态'));
    } catch (err) {
      message.error(t('保存失败: ') + ts(err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // 试运行：按当前已保存的配置预览决策，不下单
  const handleDryRun = async () => {
    setDryRunning(true);
    setDryResult(null);
    try {
      const res = await api.post('/auto-trade/dry-run');
      setDryResult(res.data.data);
    } catch (err) {
      message.error(t('试运行失败: ') + ts(err.response?.data?.error || err.message));
    } finally {
      setDryRunning(false);
    }
  };

  if (!config) {
    return error ? <Alert type="error" showIcon message={error} /> : <Typography.Text type="secondary">{t('加载中…')}</Typography.Text>;
  }

  const plan = dryResult?.plan;
  return (
    <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 760 }}>
      <Alert
        type={config.enabled ? 'success' : 'info'}
        showIcon
        message={
          config.enabled
            ? t('自动交易运行中：调度器每分钟评估一次，按价格区间与小时目标自动执行 USDT ⇄ USGNAA')
            : t('自动交易已关闭：保存配置并打开开关后生效')
        }
        description={t('价格高于区间上限时卖出 USGNAA 压价，低于下限时买入拉价，区间内小额随机双向交易维持活跃度；连续 3 次执行失败会自动停机并写入站内消息')}
      />

      {hourStats && (
        <Row gutter={24}>
          <Col span={12}>
            <Typography.Text type="secondary">{t('本小时笔数')}</Typography.Text>
            <Progress
              percent={Math.min(100, (hourStats.trades / (config.hourlyTrades || 1)) * 100)}
              format={() => `${hourStats.trades} / ${config.hourlyTrades}`}
            />
          </Col>
          <Col span={12}>
            <Typography.Text type="secondary">{t('本小时成交量（USDT）')}</Typography.Text>
            <Progress
              percent={Math.min(100, (hourStats.volume / (config.hourlyVolume || 1)) * 100)}
              format={() => `${hourStats.volume.toFixed(2)} / ${config.hourlyVolume}`}
            />
          </Col>
        </Row>
      )}

      <FieldRow label={t('自动化开关')}>
        <Switch checked={config.enabled} onChange={set('enabled')} checkedChildren={t('开_ON')} unCheckedChildren={t('关_OFF')} />
      </FieldRow>

      <FieldRow label={t('价格维持区间')} hint={t('1 USGNAA = ? USDT；越界时定向买卖拉回区间')}>
        <Space>
          <InputNumber min={0} step={0.01} value={config.priceMin} onChange={set('priceMin')} addonBefore={t('下限')} />
          <span>~</span>
          <InputNumber min={0} step={0.01} value={config.priceMax} onChange={set('priceMax')} addonBefore={t('上限')} />
        </Space>
      </FieldRow>

      <FieldRow label={t('每小时目标')} hint={t('成交量按 USDT 折算；按自然小时重置，下单时刻随机散布在整点内（间隔不规律，临近整点自动补齐目标）')}>
        <Space>
          <InputNumber min={0} value={config.hourlyVolume} onChange={set('hourlyVolume')} addonAfter="USDT" style={{ width: 180 }} />
          <InputNumber min={0} max={200} value={config.hourlyTrades} onChange={set('hourlyTrades')} addonAfter={t('笔')} style={{ width: 140 }} />
        </Space>
      </FieldRow>

      <FieldRow label={t('单笔金额区间')} hint={t('USDT 计价，区间内随机；卖出方向自动换算成 USGNAA 数量')}>
        <Space>
          <InputNumber min={0.01} value={config.minAmount} onChange={set('minAmount')} addonBefore={t('下限')} addonAfter="USDT" style={{ width: 200 }} />
          <span>~</span>
          <InputNumber min={0.01} value={config.maxAmount} onChange={set('maxAmount')} addonBefore={t('上限')} addonAfter="USDT" style={{ width: 200 }} />
        </Space>
      </FieldRow>

      <FieldRow label={t('金额形式')} hint={t('整数：下单金额取整（如 12 USDT）；小数：保留 2 位（如 12.37 USDT）')}>
        <Segmented
          value={config.amountInteger ? 'integer' : 'decimal'}
          onChange={(v) => set('amountInteger')(v === 'integer')}
          options={[
            { label: t('小数'), value: 'decimal' },
            { label: t('整数'), value: 'integer' }
          ]}
        />
      </FieldRow>

      <FieldRow label={t('交易钱包标签')} hint={t('命中任一标签的钱包参与自动交易；留空 = 全部钱包')}>
        <Space.Compact style={{ width: '100%' }}>
          <Select
            mode="multiple"
            style={{ flex: 1 }}
            placeholder={t('留空表示全部钱包')}
            value={config.tradeTags}
            onChange={set('tradeTags')}
            options={tagOptions}
            loading={tagsLoading}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={loadTags} loading={tagsLoading} title={t('刷新标签候选')} />
        </Space.Compact>
      </FieldRow>

      <FieldRow label={t('资金调度标签')} hint={t('交易钱包余额不够时，从命中这些标签的钱包自动转入 USDT/USGNAA；留空 = 不自动互转')}>
        <Space.Compact style={{ width: '100%' }}>
          <Select
            mode="multiple"
            style={{ flex: 1 }}
            placeholder={t('留空表示不自动互转')}
            value={config.transferTags}
            onChange={set('transferTags')}
            options={tagOptions}
            loading={tagsLoading}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={loadTags} loading={tagsLoading} title={t('刷新标签候选')} />
        </Space.Compact>
      </FieldRow>

      <Space>
        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
          {t('保存配置')}
        </Button>
        <Button icon={<ExperimentOutlined />} onClick={handleDryRun} loading={dryRunning}>
          {t('试运行（不下单）')}
        </Button>
        <Button icon={<ReloadOutlined />} onClick={load}>
          {t('刷新状态')}
        </Button>
      </Space>

      {plan && (
        <Alert
          type={plan.action === 'skip' ? 'warning' : 'success'}
          showIcon
          message={
            plan.action === 'trade' ? (
              <Space>
                <Tag color={plan.direction === 'buy' ? 'green' : 'red'}>
                  {plan.direction === 'buy' ? t('买入') : t('卖出')}
                </Tag>
                {t('将用 {label} 以 {amount} {sym} 下单（≈{v} USDT）', { label: plan.wallet.label, amount: plan.inputAmount, sym: plan.inputSymbol, v: plan.volumeUsdt })}
              </Space>
            ) : plan.action === 'transfer' ? (
              t('将先调度资金：{from} → {to} 转 {amount} {token}', { from: plan.sender.label, to: plan.receiver.label, amount: plan.amount, token: plan.token })
            ) : (
              t('本次将跳过：{reason}', { reason: ts(plan.reason) })
            )
          }
          description={t('当前价格 {price} USDT/USGNAA · 本小时已 {trades} 笔 / {vol} USDT', { price: Number(dryResult.price).toFixed(6), trades: dryResult.hourStats.trades, vol: dryResult.hourStats.volume.toFixed(2) }) + (plan.action !== 'skip' ? ` · ${t('依据：')}${ts(plan.reason)}` : '')}
        />
      )}
      {error && <Alert type="error" showIcon message={error} />}
    </Space>
  );
};

export default AutoTradePanel;
